import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spin } from "antd";
import { axiosRDV } from "../../../../api/axios";
import useAuthSession from "../../../../authPatient/hooks/useAuthSession";
import { selectAuthUser } from "../../../../authPatient/redux/selectors/authSelectors";
import { MEDICAL_ACCESS_SCOPE_OPTIONS } from "../../../../authorization/useMedicalCapabilities";
import { fetchDoctors } from "../../../../authDoctor/redux/thunks/doctorsThunks";
import "./AccessDoctorsCard.css";

const labelsByScope = new Map(
  MEDICAL_ACCESS_SCOPE_OPTIONS.map(({ value, label }) => [value, label]),
);

const unwrapList = (value) => {
  const payload = value?.data ?? value;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const unwrapDoctor = (value) => {
  let payload = value?.data ?? value;
  if (payload?.data) payload = payload.data;
  return payload && typeof payload === "object" ? payload : {};
};

const getPatientId = (user) =>
  String(user?.patientId ?? user?.id ?? user?._id ?? user?.userId ?? user?.sub ?? "").trim();

const getDoctorInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join("") || "D";
};

export default function AccessDoctorsCard() {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const patientId = getPatientId(user);
  const { activeSubprofileId } = useAuthSession({ auto: true });
  const [grants, setGrants] = useState([]);
  const [doctors, setDoctors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!patientId) {
      setGrants([]);
      setLoading(false);
      return;
    }

    setError("");
    try {
      const { data } = await axiosRDV.get(
        `/access-requests/patient/${encodeURIComponent(patientId)}`,
      );
      const activeGrants = unwrapList(data).filter((grant) =>
        ["pending", "accepted"].includes(String(grant?.status || "").toLowerCase()),
      );
      setGrants(activeGrants);

      const doctorIds = new Set(
        activeGrants
          .map((grant) => String(grant?.doctorId || "").trim())
          .filter(Boolean),
      );
      if (doctorIds.size === 0) {
        setDoctors({});
        return;
      }

      const doctorRows = await dispatch(fetchDoctors()).unwrap();
      const doctorProfiles = Array.isArray(doctorRows) ? doctorRows : [];
      setDoctors(
        Object.fromEntries(
          doctorProfiles
            .filter((doctor) => doctorIds.has(String(doctor?.id ?? "").trim()))
            .map((doctor) => [String(doctor.id).trim(), unwrapDoctor(doctor)]),
        ),
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to load doctor access requests.",
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, patientId]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const respond = async (grant, status) => {
    const id = String(grant?.id ?? "");
    if (!id) return;
    setBusyId(id);
    try {
      await axiosRDV.post(`/access-requests/${encodeURIComponent(id)}/respond`, {
        status,
        ...(status === "accepted"
          ? {
              scopes: Array.isArray(grant.requestedScopes)
                ? grant.requestedScopes
                : Array.isArray(grant.scopes)
                  ? grant.scopes
                  : [],
            }
          : {}),
        ...(grant.subprofileId ? { subprofileId: grant.subprofileId } : {}),
      });
      await load();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to update this access request.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (grant) => {
    const id = String(grant?.id ?? "");
    if (!id) return;
    setBusyId(id);
    try {
      await axiosRDV.post(`/access-requests/${encodeURIComponent(id)}/revoke`, {
        subprofileId: grant.subprofileId ?? null,
      });
      await load();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to revoke this doctor's access.",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="access-doctors-card" aria-labelledby="access-doctors-title">
      <header className="access-doctors-card__header">
        <span className="access-doctors-card__icon" aria-hidden="true">+</span>
        <div>
          <h3 id="access-doctors-title">Access doctors</h3>
          <p>Manage doctors’ access to this profile</p>
        </div>
        <button
          className="access-doctors-card__refresh"
          type="button"
          onClick={load}
          disabled={loading}
          title="Refresh access list"
          aria-label="Refresh access list"
        >
          ↻
        </button>
      </header>

      {error ? <p className="access-doctors-card__error" role="alert">{error}</p> : null}
      {loading ? (
        <div className="access-doctors-card__loading"><Spin size="small" /></div>
      ) : grants.length ? (
        <ul className="access-doctors-card__list">
          {grants.map((grant) => {
            const id = String(grant.id);
            const status = String(grant.status || "pending").toLowerCase();
            const doctor = doctors[String(grant.doctorId)] || {};
            const name = doctor.fullName || doctor.name || grant.doctorName || "Doctor";
            const specialty = doctor.specialty || doctor.speciality || "Medical professional";
            const avatarUrl = doctor.avatarUrl || doctor.avatar || doctor.photo || null;
            const scopes = Array.isArray(grant.requestedScopes)
              ? grant.requestedScopes
              : Array.isArray(grant.scopes)
                ? grant.scopes
                : [];

            return (
              <li className="access-doctors-card__item" key={id}>
                <div className="access-doctors-card__doctor">
                  <span className="access-doctors-card__avatar" aria-hidden="true">
                    <span className="access-doctors-card__initials">
                      {getDoctorInitials(name)}
                    </span>
                    {avatarUrl ? (
                      <img
                        className="access-doctors-card__avatar-image"
                        src={avatarUrl}
                        alt=""
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                  </span>
                  <div className="access-doctors-card__details">
                    <strong>{name}</strong>
                    <span>{specialty}</span>
                    <span className={`access-doctors-card__status access-doctors-card__status--${status}`}>
                      {status === "accepted" ? "Access granted" : "Request pending"}
                    </span>
                    {scopes.length ? (
                      <small>{scopes.map((scope) => labelsByScope.get(scope) || scope).join(" · ")}</small>
                    ) : null}
                  </div>
                </div>

                {status === "pending" ? (
                  <div className="access-doctors-card__actions">
                    <button type="button" className="access-doctors-card__accept" disabled={busyId === id} onClick={() => respond(grant, "accepted")}>
                      {busyId === id ? "…" : "Accept"}
                    </button>
                    <button type="button" className="access-doctors-card__reject" disabled={busyId === id} onClick={() => respond(grant, "rejected")}>
                      Reject
                    </button>
                  </div>
                ) : (
                  <button type="button" className="access-doctors-card__revoke" disabled={busyId === id} onClick={() => revoke(grant)}>
                    {busyId === id ? "…" : "Revoke"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="access-doctors-card__empty">No doctors have requested or received access.</p>
      )}
      {activeSubprofileId ? (
        <span className="access-doctors-card__scope">Showing this sub-profile only</span>
      ) : null}
    </section>
  );
}